import type {
  GeneratedDefinition,
  GeneratedItem,
  GeneratedItemSegmentRule,
  GeneratedSection,
  GeneratedSegment,
} from "@keydials/api-client";
import { truncateGraphemes } from "@keydials/dial-renderer";
import { removeField } from "./fieldOps";
import { collectKeys, defaultDimension, uniqueKey } from "./wizardSupport";

// ---------------------------------------------------------------------------
// Structure edits for the user app's builder (KEY-560): sections, action points (segments) and items, addressed by key.
//
// Every function takes the model and returns a new one, spreading only what it changes, so whatever a template or an admin put
// on a node the builder doesn't show travels through untouched. Keys are minted once, unique across the whole dial, and never
// change on rename. Removing a node also removes what named it (reset schedules, item rules, scopes), so a save doesn't carry
// references to something that no longer exists.
// ---------------------------------------------------------------------------

/** How much of an action point's name becomes the short label drawn on the wheel, in visible characters. */
export const SEGMENT_LABEL_MAX = 6;

/**
 * The short label an action point's name starts with: its first {@link SEGMENT_LABEL_MAX} visible characters. Counted
 * and cut by grapheme (KEY-757), so "👨‍👩‍👧 Family" keeps the whole family and "Cake 🎂" keeps the whole cake — a code-unit
 * slice left half an emoji, drawn as "�".
 */
export function segmentLabelFromName(name: string): string {
  return truncateGraphemes(name, SEGMENT_LABEL_MAX);
}

/**
 * Whether an action point's short label should still follow its name as it's typed. True while the label is empty, still reads
 * as the truncated name it came from, or is still an `S1`-style default whose name is also untouched, so a deliberate short code
 * on an already-named action point is never overwritten. Typing your own label stops it; clearing it resumes.
 */
export function labelFollowsName(label: string, name: string): boolean {
  return (
    !label ||
    label === segmentLabelFromName(name) ||
    // A label cut before KEY-757 by code unit still counts as following, so it keeps tracking the name it came from.
    label === name.slice(0, SEGMENT_LABEL_MAX) ||
    (/^S\d+$/.test(label) && /^Segment \d+$/.test(name))
  );
}

/** A model with a node added, and the key the node was given, so the builder can select it. */
export interface AddedNode {
  model: GeneratedDefinition;
  key: string;
}

function moveWithin<T>(list: readonly T[], from: number, to: number): T[] {
  if (from < 0 || from >= list.length) return [...list];
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(to, next.length)), 0, moved);
  return next;
}

const mapSection = (model: GeneratedDefinition, sectionKey: string, change: (s: GeneratedSection) => GeneratedSection) => ({
  ...model,
  sections: model.sections.map((s) => (s.key === sectionKey ? change(s) : s)),
});

const mapSegment = (
  model: GeneratedDefinition,
  sectionKey: string,
  segmentKey: string,
  change: (g: GeneratedSegment) => GeneratedSegment,
) => mapSection(model, sectionKey, (s) => ({ ...s, segments: s.segments.map((g) => (g.key === segmentKey ? change(g) : g)) }));

// ---- references ---------------------------------------------------------------------------------------------------------

const underPath = (path: string, removed: string) => path === removed || path.startsWith(`${removed}/`);

/**
 * A scope list without the removed paths. An empty scope means "everywhere", so a list that would be emptied is left as it
 * was: silently widening a scale or a field to every item is worse than a stale path the readiness check can report.
 */
function withoutPaths(list: string[] | null | undefined, removed: string): string[] | null | undefined {
  if (!list || list.length === 0) return list;
  const kept = list.filter((path) => !underPath(path, removed));
  return kept.length === 0 || kept.length === list.length ? list : kept;
}

/** The dial with everything that named `removedPath` ("section", "section/segment" or "section/segment/item") taken out. */
function dropReferences(model: GeneratedDefinition, removedPath: string): GeneratedDefinition {
  const [sectionKey, segmentKey, itemKey] = removedPath.split("/");
  const next: GeneratedDefinition = { ...model };

  if (model.dimensions) {
    next.dimensions = model.dimensions.map((d) => {
      const scope = withoutPaths(d.appliesToItemKeys, removedPath);
      return scope === d.appliesToItemKeys ? d : { ...d, appliesToItemKeys: scope };
    });
  }

  if (model.nodeAttributes) {
    next.nodeAttributes = model.nodeAttributes.map((a) => {
      const appliesTo = withoutPaths(a.appliesToKeys, removedPath);
      const shownAt = withoutPaths(a.shownAtKeys, removedPath);
      return appliesTo === a.appliesToKeys && shownAt === a.shownAtKeys ? a : { ...a, appliesToKeys: appliesTo, shownAtKeys: shownAt };
    });
  }

  if (model.resetSchedules) {
    next.resetSchedules = model.resetSchedules.filter(
      (r) =>
        !(
          r.sectionKey === sectionKey &&
          (segmentKey === undefined || r.segmentKey === segmentKey) &&
          (itemKey === undefined || r.itemKey === itemKey)
        ),
    );
  }

  if (model.itemSegmentRules) {
    next.itemSegmentRules =
      itemKey === undefined
        ? model.itemSegmentRules.filter((r) => !(r.sectionKey === sectionKey && (segmentKey === undefined || r.segmentKey === segmentKey)))
        : model.itemSegmentRules.map((r) => (r.sectionKey === sectionKey && r.segmentKey === segmentKey ? withoutItemInRule(r, itemKey) : r));
  }

  return next;
}

/**
 * A rule's rungs without an item. A rung's item keys narrow it; with none left it would apply to every item, so a rung that
 * named only the removed item is dropped rather than widened. It could never match again anyway.
 */
function withoutItemInRule(rule: GeneratedItemSegmentRule, itemKey: string): GeneratedItemSegmentRule {
  if (!rule.overrides.some((o) => o.itemKeys?.includes(itemKey))) return rule;
  return {
    ...rule,
    overrides: rule.overrides.flatMap((o) => {
      if (!o.itemKeys?.includes(itemKey)) return [o];
      const kept = o.itemKeys.filter((k) => k !== itemKey);
      return kept.length === 0 ? [] : [{ ...o, itemKeys: kept }];
    }),
  };
}

// ---- sections -------------------------------------------------------------------------------------------------------------

/** Adds a section at the end. Its key is a short letter (A, B, C…) so it reads as a tidy mnemonic. */
export function addSection(model: GeneratedDefinition, label = ""): AddedNode {
  const n = model.sections.length;
  const key = uniqueKey(n < 26 ? String.fromCharCode(65 + n) : `S${n + 1}`, collectKeys(model));
  return { model: { ...model, sections: [...model.sections, { key, label, segments: [] }] }, key };
}

export const updateSection = (
  model: GeneratedDefinition,
  sectionKey: string,
  patch: Partial<Pick<GeneratedSection, "label" | "description" | "colour">>,
): GeneratedDefinition => mapSection(model, sectionKey, (s) => ({ ...s, ...patch }));

export function removeSection(model: GeneratedDefinition, sectionKey: string): GeneratedDefinition {
  if (!model.sections.some((s) => s.key === sectionKey)) return model;
  return dropReferences({ ...model, sections: model.sections.filter((s) => s.key !== sectionKey) }, sectionKey);
}

export function moveSection(model: GeneratedDefinition, sectionKey: string, toIndex: number): GeneratedDefinition {
  const from = model.sections.findIndex((s) => s.key === sectionKey);
  return from < 0 ? model : { ...model, sections: moveWithin(model.sections, from, toIndex) };
}

// ---- action points --------------------------------------------------------------------------------------------------------

/** Adds an action point at the end of a section. Its short label starts as the truncated name. */
export function addSegment(model: GeneratedDefinition, sectionKey: string, name = ""): AddedNode {
  if (!model.sections.some((s) => s.key === sectionKey)) return { model, key: "" };
  const used = collectKeys(model);
  const key = uniqueKey(`${sectionKey}-seg-${used.size}`, used);
  const segment: GeneratedSegment = { key, label: segmentLabelFromName(name), name, items: [] };
  return { model: mapSection(model, sectionKey, (s) => ({ ...s, segments: [...s.segments, segment] })), key };
}

/** Renames an action point. Its short label follows while {@link labelFollowsName} says it should. */
export const renameSegment = (model: GeneratedDefinition, sectionKey: string, segmentKey: string, name: string): GeneratedDefinition =>
  mapSegment(model, sectionKey, segmentKey, (g) => ({
    ...g,
    name,
    label: labelFollowsName(g.label, g.name) ? segmentLabelFromName(name) : g.label,
  }));

export const updateSegment = (
  model: GeneratedDefinition,
  sectionKey: string,
  segmentKey: string,
  patch: Partial<Pick<GeneratedSegment, "label" | "guidance">>,
): GeneratedDefinition => mapSegment(model, sectionKey, segmentKey, (g) => ({ ...g, ...patch }));

export function removeSegment(model: GeneratedDefinition, sectionKey: string, segmentKey: string): GeneratedDefinition {
  const section = model.sections.find((s) => s.key === sectionKey);
  if (!section?.segments.some((g) => g.key === segmentKey)) return model;
  return dropReferences(
    mapSection(model, sectionKey, (s) => ({ ...s, segments: s.segments.filter((g) => g.key !== segmentKey) })),
    `${sectionKey}/${segmentKey}`,
  );
}

export function moveSegment(model: GeneratedDefinition, sectionKey: string, segmentKey: string, toIndex: number): GeneratedDefinition {
  return mapSection(model, sectionKey, (s) => {
    const from = s.segments.findIndex((g) => g.key === segmentKey);
    return from < 0 ? s : { ...s, segments: moveWithin(s.segments, from, toIndex) };
  });
}

// ---- items ----------------------------------------------------------------------------------------------------------------

/** Adds an item at the end of an action point's checklist. */
export function addItem(model: GeneratedDefinition, sectionKey: string, segmentKey: string, name = ""): AddedNode {
  const segment = model.sections.find((s) => s.key === sectionKey)?.segments.find((g) => g.key === segmentKey);
  if (!segment) return { model, key: "" };
  const used = collectKeys(model);
  const key = uniqueKey(`${segmentKey}-item-${used.size}`, used);
  return { model: mapSegment(model, sectionKey, segmentKey, (g) => ({ ...g, items: [...g.items, { key, name }] })), key };
}

export const updateItem = (
  model: GeneratedDefinition,
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
  patch: Partial<Omit<GeneratedItem, "key">>,
): GeneratedDefinition =>
  mapSegment(model, sectionKey, segmentKey, (g) => ({ ...g, items: g.items.map((i) => (i.key === itemKey ? { ...i, ...patch } : i)) }));

export function removeItem(model: GeneratedDefinition, sectionKey: string, segmentKey: string, itemKey: string): GeneratedDefinition {
  const segment = model.sections.find((s) => s.key === sectionKey)?.segments.find((g) => g.key === segmentKey);
  if (!segment?.items.some((i) => i.key === itemKey)) return model;
  return dropReferences(
    mapSegment(model, sectionKey, segmentKey, (g) => ({ ...g, items: g.items.filter((i) => i.key !== itemKey) })),
    `${sectionKey}/${segmentKey}/${itemKey}`,
  );
}

export function moveItem(
  model: GeneratedDefinition,
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
  toIndex: number,
): GeneratedDefinition {
  return mapSegment(model, sectionKey, segmentKey, (g) => {
    const from = g.items.findIndex((i) => i.key === itemKey);
    return from < 0 ? g : { ...g, items: moveWithin(g.items, from, toIndex) };
  });
}

// ---- the fork -------------------------------------------------------------------------------------------------------------

/** A model switched between Simple and Itemised, and the labels of the item fields the switch removed. */
export interface KindChange {
  model: GeneratedDefinition;
  removedFieldLabels: string[];
}

/**
 * Switches between one colour per action point ("Simple") and a checklist inside each ("Itemised"). Going to Simple removes
 * every item, the scoring scales, and every field asked of each item (with what named them), since there are no items left to
 * ask; the builder confirms first, and the labels returned let the confirm name the fields (decision 7). The edit is pure, so
 * the confirm can run it on the current model to find them. Going to Itemised gives the dial its one Status scale in its own
 * colours.
 */
export function setKind(model: GeneratedDefinition, kind: "Simple" | "Itemised"): KindChange {
  if (kind === "Simple") {
    let next: GeneratedDefinition = {
      ...model,
      kind,
      dimensions: undefined,
      sections: model.sections.map((s) => ({ ...s, segments: s.segments.map((g) => (g.items.length === 0 ? g : { ...g, items: [] })) })),
    };
    const itemFields = (model.nodeAttributes ?? []).filter((a) => a.target === "Item");
    for (const field of itemFields) next = removeField(next, field.target, field.key).model;
    return { model: next, removedFieldLabels: itemFields.map((a) => a.label) };
  }
  return {
    model: { ...model, kind, dimensions: model.dimensions?.length ? model.dimensions : [defaultDimension(model.statusSet.statuses)] },
    removedFieldLabels: [],
  };
}

/** Whether switching to Simple would lose anything the author typed. */
export const hasItems = (model: GeneratedDefinition): boolean => model.sections.some((s) => s.segments.some((g) => g.items.length > 0));
